export const documents = {
  title: "Documents",
  description: "Manage and view your documents",
  newDocument: {
    trigger: "New document",
    title: "New document",
    searchPlaceholder: "Search templates…",
    listLabel: "Document templates",
    noResults: "No templates match your search",
    noResultsHint: "Try a different word, or clear the search to see every template.",
    aboutTemplate: "About this template",
    sections: {
      franklin: "Franklin templates",
    },
    templates: {
      coveredWorksAct: {
        name: "Covered works act",
        fullName: "Covered works act (Kaetud tööde akt)",
        details:
          "Records works before they are covered up, so what is behind a wall or under a screed stays evidenced. Signed digitally in Franklin, and printed on the official Kaetud tööde akt form.",
        description:
          "Record works that are about to be covered up, with photos and the parties who inspected them.",
      },
      constructionDiary: {
        name: "Construction diary",
        fullName: "Construction diary (Ehituspäevik)",
        details:
          "The day's site record — works done, weather, people on site and deliveries. Printed on the official Ehituspäevik form, one numbered page per day.",
        description: "Daily site log of works, weather, people on site and deliveries.",
      },
      moreFranklinTemplates: {
        name: "More Franklin templates",
        fullName: "More Franklin templates",
        details:
          "More Franklin templates are on the way. This card is a placeholder and cannot be opened yet.",
        description: "Further Franklin templates are on the way.",
      },
    },
  },
  templateForm: {
    back: "Back to templates",
    preview: "Preview",
    documentNumber: "No.",
    documentNumberHint: "Assigned when the document is filed.",
    documentName: "Document name",
    choosePrefilled: "Choose pre-filled form",
    importFrom: "Import",
    searchSavedForms: "Search saved forms…",
    noSavedForms: "No saved forms yet.",
    savedFormsNeedProject:
      "Saved forms belong to a project — this document's project is chosen when saving, so none can be offered here.",
    savedFormsProject: "Project",
    savedFormsPickProject: "Choose a project to see the forms already filed in it.",
    prefilledFrom: "Filled in from {name}. The number and name stay this form's own.",
    importedFrom: "{field} taken from {name}.",
    importedNothing: "{name} has nothing in that field.",
    addPerson: "Add person",
    removePerson: "Remove person",
    personName: "Name",
    personCompany: "Company",
    personCertificate: "Certificate no.",
    personSigner: "Signatory",
    pickMember: "Pick a member",
    searchMembers: "Search members…",
    noMembers: "No members found.",
    loadingMembers: "Loading members…",
    membersError: "Couldn't load members.",
    retryMembers: "Try again",
    membersNeedProject:
      "Members belong to a project — this document's project is chosen when saving. Type the name in instead.",
    uploadFiles: "Upload files",
    chooseFromProject: "Choose file from project",
    loadingDocuments: "Loading documents…",
    documentsError: "Couldn't load the project's documents.",
    removeAttachment: "Remove attachment",
    noAttachments: "No files attached yet.",
    duplicatesSkipped: "Skipped {count} file(s) that are already attached.",
    time: {
      hours: "Hour",
      minutes: "Min",
      clear: "Clear",
      placeholder: "--:--",
    },
    attachToPdf: "Add attachments to the PDF and digital document",
    signDigitally: "Form will be signed digitally",
    pdfLanguage: {
      label: "PDF language",
      et: "Estonian",
      en: "English",
    },
    outputHints: {
      attachToPdf:
        "Attached PDFs and photos are appended to the generated document as extra pages, so they travel with it — including into the signed container. Files that cannot be drawn on a page, such as DWG drawings or spreadsheets, stay listed on the form by name and are saved as separate documents. Unticked, no attachments are added to or listed on the PDF.",
      signDigitally:
        "After saving, the document opens for signing with Smart-ID, Mobile-ID or ID-card and is stored as a signed .asice container. The people you switch to Signatory are printed under the signature block of the form itself.",
    },
    save: "Save",
    saving: "Saving…",
    discard: {
      title: "Discard this form?",
      description:
        "Everything you have entered will be lost. Nothing has been saved to this project yet.",
      confirm: "Discard",
      cancel: "Keep editing",
    },
    destination: {
      title: "Where should this document go?",
      description: "Choose the project and folder to file it in.",
      submit: "Save here",
    },
    saved: {
      title: "Form saved",
      description: "{name} is now in this project's documents.",
      attachmentsNotEmbedded:
        "These files are listed on the form but could not be added to the PDF itself: {names}",
    },
    saveFailed: {
      title: "Couldn't save the form",
      steps: {
        number: "Another document took this number. Try saving again.",
        reserve: "The document number could not be reserved.",
        attachments: "The attachments could not be uploaded.",
        pdf: "The PDF could not be generated.",
        file: "The PDF could not be stored.",
        record: "The document was stored but the form could not be linked to it.",
      },
    },
    validation: {
      missingFields: "Still to fill in: {fields}",
      missingName: "Give the document a name.",
      noProject: "This building has no project yet. Create one before filing documents.",
      notReady: "Your account is still loading. Try again in a moment.",
    },
    overwrite: {
      title: "A document with this name already exists",
      description:
        "{name} is already filed here. Saving adds this form as a new version of it, and what is there now stops being the version people see.",
      confirm: "File as a new version",
      cancel: "Change the name",
    },
    signing: {
      unavailable: "Digital signing is not switched on for your organisation.",
      startTitle: "Ready to sign",
      startDescription: "The form is saved. Sign it now, or from the documents list later.",
    },
    pdf: {
      numberLabels: {
        act: "Act no.:",
        page: "Page no.:",
        document: "Document no.:",
      },
      date: "2. Date:",
      contractor: "1. Contractor",
      digitallySigned: "/digitally signed/",
      failed: "Could not generate the PDF.",
      signatureColumns: {
        nameAndCompany: "Name, company",
        certificate: "Certificate",
      },
      weatherGrid: {
        time: "Time",
        temperature: "Temp",
      },
      additionalData: "Additional information",
      // The English rendering of the statutory rows, for a reader who does not
      // work in Estonian. The Estonian PDF remains the one that gets filed.
      rows: {
        siteAndContract: "Name and location of the works / Contract no.",
        worksDescription: "Name and location of the covered works",
        conformityAssessment:
          "Reasoned assessment of the covered works against applicable requirements and the design",
        otherWorkData: "Other information about the works, where needed",
        inspectionMethod:
          "Description of the method or equipment used to inspect the covered works",
        inspectionTime: "Time of the covered-works inspection",
        attachments: "Annexes to the act",
        author: "Name of the person making the entry",
        weather: "Weather",
        otherFactors:
          "Other influencing factors\n(e.g. room temperature, humidity, ambient conditions)",
        worksPerformed: "Description of the works performed",
        materials: "Construction materials and products used",
        coveredWorksActs: "Covered-works acts",
        wasteRemovalDate: "Date of waste removal",
        supervisorNote: "Owner's supervisor's remark or recommendation, where given",
        stateSupervisionOrder: "State supervisor's precept, where issued",
        contractorSignatories: "Name of the contractor and their competent person",
        supervisorSignatories: "Name of the owner's supervisor and their competent person",
      },
    },
    sections: {
      general: { title: "General" },
      coveredWorks: {
        title: "Covered works",
        help: "Describe the works before they are covered up, where they are, and what they follow.",
      },
      conditions: { title: "Conditions", help: "Site conditions at the time of the inspection." },
      diaryWorks: { title: "Works", help: "What happened on site today." },
      diaryNotes: { title: "Notes" },
      parties: { title: "Parties", help: "Everyone who took part. Mark who signs the document." },
      attachments: {
        title: "Attachments",
        help: "Photos, measurements and any file that belongs with the form.",
      },
    },
    fields: {
      date: "Date",
      buildingName: "Building",
      buildingAddress: "Address",
      contractNumber: "Contract number",
      organisation: "Organisation",
      author: "Entry author",
      worksDescription: "Name and location of the covered works",
      conformityAssessment: "Conformity assessment",
      otherWorkData: "Other information about the works",
      inspectionMethod: "Inspection method or equipment",
      otherFactors: "Other influencing factors",
      coveredWorksActs: "Covered-works acts",
      wasteRemovalDate: "Date of waste removal",
      supervisorNote: "Owner's supervisor's remark",
      stateSupervisionOrder: "State supervisor's precept",
      contractorSignatories: "Contractor",
      supervisorSignatories: "Owner's supervisor",
      worksLocation: "Location on site",
      worksStart: "Works started",
      worksEnd: "Works finished",
      materials: "Materials used",
      standards: "Standards and requirements",
      weather: "Weather",
      temperature: "Temperature (°C)",
      inspectionTime: "Time",
      worksPerformed: "Works performed",
      workforce: "People on site",
      machinery: "Machinery",
      deliveries: "Deliveries",
      notes: "Notes",
      instructions: "Instructions given",
      attachments: "Attachments",
    },
    weather: {
      strongWind: "Strong wind",
      dry: "Dry",
      rain: "Rain",
      sleet: "Sleet",
      snow: "Snow",
    },
  },
  list: {
    title: "Document List",
    name: "Document",
    description: "Document description",
    type: "Type",
    size: "Size",
    uploadDate: "Upload date",
    version: "Version",
    actions: "Actions",
    search: "Search documents...",
    noDocuments: "No documents match your search",
    noFilterMatch: "No documents match the current filters. Try adjusting your filter criteria.",
    error: "Error loading documents",
    select: "Select",
    date: "Date Created",
    versions: "versions",
  },
  upload: {
    title: "Upload documents",
    button: "Upload",
    dragAndDrop: "Drag files here or click to select files",
    multipleAllowed: "Multiple files allowed",
    maxSize: "Maximum file size: 50MB",
    uploadSuccess: "Upload successful",
    uploadError: "Upload failed",
    success: "Upload successful",
    successMessage: "Document uploaded successfully",
    dataExtracted: "Data extracted",
    dataExtractedMessage: "Data has been extracted from the document",
    currentCategory: "Current category",
    currentFolder: "Current folder",
    selectMode: "Select upload mode",
    singleFile: "Single file",
    multipleFiles: "Multiple files",
    multipleDescription: "Select multiple files to upload at once",
    selectFiles: "Select files",
    uploading: "Uploading files",
    foldersUploaded: "Folders uploaded: {count}",
    folderCreationFailedTitle: "Couldn't create the folders",
    folderCreationFailedDescription: "Nothing was uploaded. {reason}",
  },
  actions: {
    download: "Download",
    delete: "Delete",
    process: "Process",
    deleteSuccess: "Document deleted successfully",
    reanalyze: "Reanalyse",
    categorize: "Categorise",
    categorizeSuccess: "Document categorised successfully",
    categorizedAs: "Categorised as",
    categorizeFailed: "Failed to categorise document",
    move: "Move",
    moving: "Moving...",
    moveSuccess: "Documents moved successfully",
    movedTo: "moved to folder",
    moveFailed: "Failed to move documents",
    clear: "Clear",
    sorting: "Sorting",
    close: "Close",
    create: "Create",
    upload: "Upload",
    view: "View",
    back: "Back",
    refreshSuccess: "Documents refreshed successfully",
    deleteDocument: "Delete Document",
    deleteDocumentTitle: "Delete Document?",
    deleteDocumentConfirm:
      'Are you sure you want to delete "{filename}"? This action cannot be undone.',
    deleteDocumentConfirmFallback:
      "Are you sure you want to delete this document? This action cannot be undone.",
    downloading: "Downloading",
    downloadingFile: 'Downloading "{filename}"...',
    preparingDownload: "Preparing {count} files for download...",
    copy: "Copy",
  },
  category: {
    all: "All",
    drawings: "Drawings",
    design_documents: "Design Documents",
    contracts: "Contracts",
    permits: "Permits",
    certificates: "Certificates",
    reports: "Reports",
    invoices: "Invoices",
    other: "Other",
    unsorted: "Unsorted",
    documents: "Documents",
  },
  categories: {
    all: "documents",
    documents: "Documents",
    drawings: "Drawings",
    unsorted: "Unsorted",
    predefinedFolders: "Predefined Folders",
  },
  status: {
    processing: "Document Processing",
    processingFailed: "Couldn't process this document",
    unsupportedFiletype: "Unsupported filetype",
    zipFile: "Zip file",
    teachFranklin: "Click to Teach Franklin",
    editTags: "Edit tags",
    addTags: "Add tags",
  },
  preview: {
    unavailable: "PDF preview unavailable",
    unavailableDesc:
      "Your browser couldn't display this PDF inline. You can still download the file or open it in a new tab.",
    dwgDisclaimer: "Preview exports are provided for reference only",
  },
  folder: {
    title: "Folders",
    create: "Create folder",
    name: "Folder name",
    type: "Folder type",
    actions: "Actions",
    root: "Root",
    noFolders: "No folders found",
    newFolder: "New Folder",
    rename: "Rename",
    delete: "Delete",
    deleteFolder: "Delete Folder",
    deleteConfirm: "Are you sure you want to delete this folder? This action cannot be undone.",
    deleteSuccess: "Folder deleted successfully",
    deleteError: "Failed to delete folder",
    createError: "Failed to create folder",
    noDocuments: "This folder is empty",
    categories: {
      documents: "Documents",
      drawings: "Drawings",
      unsorted: "Unsorted",
    },
    totalDocuments: "documents",
    new: "New Folder",
    uploadFiles: "Upload Files",
    documentCount: "{count} document(s)",
    documentCountOne: "1 document",
    subfolderCount: "{count} subfolder(s)",
    createSubfolder: "Create Subfolder",
    exportFolder: "Export Folder",
    shareFolder: "Share Folder",
    folderActions: "Folder actions",
    dropHereToMove: "Drop here to move",
    releaseToMove: "Release to move",
    draggingItems: "Moving {count} item(s). Drop on a highlighted folder.",
  },
  uploadModal: {
    title: "Upload Documents",
    dragDropFiles: "Drag & drop files here",
    dropFilesHere: "Drop files here",
    orClickToBrowse: "or click to browse",
    uploadFiles: "Upload Files",
    uploadFolder: "Upload Folder",
    readFailed: "Couldn't read the selected items. Please try again.",
    uploadingTo: "Uploading to",
    chooseDestination: "Choose where to upload",
    changeDestination: "Change",
    destinationTitle: "Choose upload destination",
    destinationDescription: "Pick the folder these files go into, or keep the project root.",
    destinationSubmit: "Use this folder",
    filesReady: "{count} file(s) ready to upload",
    foldersReady: "{count} folder(s) ready to upload",
    upload: "Upload",
  },
  destinationPicker: {
    title: "Choose a folder",
    submit: "Upload here",
    cancel: "Cancel",
    descriptionWithFile:
      "Pick where to save “{filename}”. You can also drop it at the project root.",
    description: "Pick a project and folder. You can also drop the file at the project root.",
    projectLabel: "Project",
    projectFallback: "Project",
    newProject: "New project",
    projectNamePlaceholder: "Project name",
    selectProject: "Select a project…",
    defaultProject: "(default)",
    noFolders: "No folders yet",
    selectProjectToBrowse: "Select a project to browse folders",
    newFolderHere: "New folder here",
    folderNamePlaceholder: "Folder name",
    inFolder: "In {path}",
    inSelectedFolder: "In the selected folder",
    inProjectRoot: "In {project} (project root)",
    folderCreated: "Folder created",
    folderCreateFailed: "Couldn't create folder",
    contentsLoadFailed: "Couldn't load this folder's contents.",
    tryAgain: "Try again",
    loadingContents: "Loading folder contents",
    emptyFolder: "Empty folder — your file will be saved here.",
    existingFile: "Existing file in this folder",
    destination: "Destination:",
    selectedFolderFallback: "— selected folder",
    pickProjectToEnable: "Pick a project to enable upload",
  },
  multiSelect: {
    selected: "selected",
    moveTo: "Move to...",
    selectDestination: "Select destination",
    noSelection: "No documents selected",
  },
  versions: {
    latest: "Latest",
    showAll: "Show all versions",
    hideAll: "Hide versions",
    compare: "Compare",
    versionCount: "versions",
  },
  comparison: {
    title: "Document comparison",
    compare: "Compare",
    sideBySide: "Side-by-side view",
    tabbed: "Tabbed view",
    notEnoughVersions: "At least two versions are required for comparison",
    loadError: "Failed to load comparison documents",
    close: "Close",
    analysisTitle: "Difference Analysis",
    analyzing: "Analysing differences...",
    noChanges: "No significant changes detected",
    wordDocFallback: "Word document comparison not available",
    selectVersions: "Select 2 versions to compare",
  },
  search: {
    title: "Search",
    placeholder: "Search documents...",
    noDocuments: "No documents match your search",
    scope: {
      currentView: "in current view",
      folder: 'in "{folder}"',
      allDocuments: "in all documents",
    },
    toggleLabel: "Search all documents in building",
    sortLabel: "Sort by:",
    result: "result",
    results: "results",
    contentPartial: "Some matches in document text may be missing. Narrow your search to see them.",
    contentUnavailable:
      "Search in document text is unavailable right now. These results match file names only.",
  },
  chat: {
    title: "Document Chat",
    placeholder: "What do you need to find?",
    error: "Failed to process query",
    examples: ["Find all architecture files", "Show me the floor plans", "Count all contracts"],
    empty:
      'Chat with your documents. Ask questions like "Find all architecture files" or "Show me the floor plans"',
    noDocuments: "No documents match your search",
  },
  filters: {
    title: "Filters",
    all: "All",
    recent: "Recent",
    search: "Search Results",
    filterByType: "Filter by type",
    clear: "Clear filters",
    showing: "Showing {filtered} of {total} documents",
    active: "Active filters",
    filtered: "Filtered",
    allTypes: "All types",
  },
  tabs: {
    documents: "Documents",
    chat: "Chat",
    upload: "Upload",
    analytics: "Analytics",
  },
  emptyState: {
    title: "No Documents",
    description: "Upload your first document to get started",
    uploadButton: "Upload Document",
  },
  uploadOrDrop: "Upload or drop files",
  uploadComplete: "Upload complete",
  documentCounts: {
    total: "Total Documents",
    pdf: "PDF Documents",
    word: "Word Documents",
    excel: "Excel Documents",
    image: "Image Files",
  },
  viewer: {
    download: "Download",
    openInNewTab: "Open in new tab",
    zoomIn: "Zoom in",
    zoomOut: "Zoom out",
    fitWidth: "Fit width",
    page: "Page",
    loading: "Loading PDF…",
  },
  analytics: {
    storageUsage: "Storage Usage (MB)",
  },
  uploadProgress: {
    analyzing: "Analysing...",
    uploading: "Uploading...",
    complete: "Complete",
    failed: "Failed",
  },
  processingBar: {
    title: "File processing",
    message:
      "Franklin is tagging your uploaded files with metadata so they can be sorted and your data stays organised. This runs automatically — you can keep working.",
    documentsProcessing: "{count} document(s) processing",
  },
  uploadQueue: {
    receivingFiles: "Receiving your files…",
    receivedFiles: "All your files are in.",
    receivedFilesWithFailures: "I received {completed} of {total} files.",
    receivingShort: "Receiving your files…",
    processingMessage: "All {count} files are in. I’m reading and tagging them now.",
    processingMessageOne: "Your file is in. I’m reading and tagging it now.",
    uploadingFiles: "Uploading files",
    uploadComplete: "Upload complete",
    filesProgress: "{completed} of {total} files",
    done: "done",
    uploading: "uploading",
    pending: "pending",
    failed: "failed",
    skipped: "skipped",
    retryFailed: "Retry failed ({count})",
    processingDocuments: "Processing {count} documents",
    extractingText: "Extracting text…",
    extractingProgress: "Extracting text · {done} of {total} done",
    processingShort: "I’m reading {count} files…",
    notifyWhenDone: "Tell me when it’s done →",
    notifySubscribed: "I’ll let you know.",
    minimize: "Minimise",
    expand: "Expand",
    dismiss: "Dismiss",
    cancelAll: "Cancel all",
    close: "Close",
  },
  classification: {
    inProgress: "AI Classification in Progress",
    complete: "Classification Complete",
    failed: "Classification Failed",
    processing: "AI Classification Processing",
    preparing: "Preparing for Classification",
    runningMessage:
      "AI is classifying your uploaded files. Files will be moved to appropriate folders automatically.",
    completedMessage:
      "Classification complete! Files have been organised into their appropriate folders.",
    failedMessage: "Classification failed. Files will remain in their current location.",
    preparingMessage:
      "Preparing to classify uploaded files automatically. This may take a few moments...",
    uploadNotAvailable: "Upload Not Available",
    setupRequired: "Setup Required",
    userProfileError: "User profile not loaded. Please ensure you are logged in.",
    organizationError:
      "No organisation selected. Please select an organisation before uploading files.",
    loginPrompt: "Please ensure you are logged in.",
    organizationPrompt: "Please select an organisation to upload files.",
  },
  tags: {
    added: "Tag Added",
    removed: "Tag Removed",
    error: "Tag Error",
    tagAdded: "Tag added",
    tagRemoved: "Tag removed",
    addError: "Failed to add tag",
    removeError: "Failed to remove tag",
    searchPlaceholder: "Search tags...",
    noOptions: "No tag options available",
    createCustom: "Create custom tag",
    projects: "Projects",
    folders: "Folders",
    custom: "Custom Tags",
  },
  log: {
    title: "Documents Log",
    subtitle: "Activity log for {buildingName}",
    backButton: "Back to Documents",
    searchPlaceholder: "Search files...",
    allTypes: "All types",
    newFile: "New file",
    updated: "Updated",
    deleted: "Deleted",
    restored: "Restored",
    moved: "Moved",
    allUsers: "All users",
    allTags: "All tags",
    fromDate: "From date",
    toDate: "To date",
    clearButton: "Clear",
    typeHeader: "Type",
    fileNameHeader: "File Name",
    dateTimeHeader: "Date & Time",
    changedByHeader: "Changed By",
    tagsHeader: "Tags",
    loadingActivities: "Loading activities...",
    noActivitiesMatched: "No activities match your filters",
    noActivities: "No activities found",
    noTags: "No tags",
    history: {
      title: "Document history",
      actionLabel: "Document history",
      projectSubtitle: "Project — {name}",
      buildingSubtitle: "Building — {name}",
      timelineLabel: "Document activity timeline",
      searchPlaceholder: "Search documents, people, or filenames",
      filterLabel: "Filter document activity",
      filters: {
        all: "All activity",
        added: "Added",
        edited: "Edited",
        renamed: "Renamed",
        deleted: "Deleted",
        restored: "Restored",
      },
      actorFilterLabel: "Filter by person",
      anyone: "Anyone",
      filterSections: {
        activity: "Activity",
        person: "Person",
        fileType: "File type",
        date: "Date",
      },
      fileTypeFilterLabel: "Filter by file type",
      allFileTypes: "All file types",
      unknownFileType: "Other files",
      dateFilter: {
        label: "Filter by date",
        anyTime: "Any time",
        today: "Today",
        last7Days: "Last 7 days",
        last30Days: "Last 30 days",
        customRange: "Custom range",
        apply: "Apply",
        clear: "Clear the date filter",
      },
      verbs: {
        added: "added",
        edited: "edited",
        renamed: "renamed",
        deleted: "deleted",
        restored: "restored",
      },
      eventTypes: {
        added: "Added event",
        edited: "Edited event",
        renamed: "Renamed event",
        deleted: "Deleted event",
        restored: "Restored event",
      },
      changes: {
        fields: {
          filename: "Filename",
          categories: "Categories",
          disciplines: "Disciplines",
          stages: "Stages",
        },
        before: "Before",
        after: "After",
        none: "None",
      },
      relativeTime: {
        justNow: "just now",
        minutes: "{count} min ago",
        hours: "{count} h ago",
        days: "{count} d ago",
        weeks: "{count} wk ago",
        months: "{count} mo ago",
      },
      dayGroups: {
        today: "Today",
        yesterday: "Yesterday",
      },
      version: "Version {version}",
      unknownUser: "Unknown user",
      loading: "Loading documents history…",
      emptyProject: "No document activity in this project yet.",
      emptyBuilding: "No document activity in this building yet.",
      filteredEmpty: "No activity matches that filter.",
      filteredEmptyLoaded:
        "No loaded activity matches that filter. Older activity may match: use Load older to search further back.",
      clearFilters: "Clear filters",
      loadError: "Couldn't load document history.",
      retry: "Try again",
      loadOlder: "Load older",
      loadingOlder: "Loading older…",
      eventCount: "{shown} of {total} events",
      open: "Open",
      download: "Download",
      versionHistory: "Version history",
      restore: "Restore",
      close: "Close document history",
      resizePanel: "Resize document history panel",
      downloadErrorTitle: "Download failed",
      downloadError: "Could not download the document.",
      restoreSuccess: "Document restored",
      restoreErrorTitle: "Restore failed",
      restoreError: "Could not restore the document.",
    },
  },
  manage: {
    subtitle: "Drop files anywhere to upload and manage documents",
    manageShares: "Manage Shares",
  },
  trash: {
    title: "Trash",
    description: "Deleted documents are stored here for 30 days before being permanently removed",
    backButton: "Back to Documents",
    loadingTrash: "Loading trash...",
    emptyTitle: "Trash is empty",
    emptyDescription:
      "When you delete documents, they'll appear here where you can restore them within 30 days.",
    purgeWarning: "Documents will be automatically purged after 30 days",
    restore: "Restore",
    restoreDocumentTitle: "Restore Document?",
    restoreDocumentConfirm: 'Restore "{filename}" from trash?',
    restoreDocumentConfirmFallback: "Restore this document from trash?",
    documentCount: "{count} document(s)",
    nameColumn: "Name",
    deletedColumn: "Deleted",
    sizeColumn: "Size",
    selectAll: "Select all",
    selectedCount: "{count} selected",
    clearSelection: "Clear selection",
    permanentDelete: "Delete permanently",
    permanentDeleteTitle: "Delete permanently?",
    permanentDeleteConfirm: 'Permanently delete "{filename}"? This action cannot be undone.',
    permanentDeleteConfirmFallback:
      "Permanently delete this document? This action cannot be undone.",
    bulkPermanentDeleteTitle: "Delete permanently?",
    bulkPermanentDeleteConfirm:
      "Permanently delete {count} document(s)? This action cannot be undone.",
    confirmPermanentDelete: "Delete permanently",
    emptyTrashButton: "Empty trash",
    emptyTrashTitle: "Empty trash?",
    emptyTrashConfirm:
      "Permanently delete all {count} document(s) in the trash? This action cannot be undone.",
  },
  bulkActions: {
    selectedCount: "documents selected",
    signature: "Signature",
    share: "Share",
    download: "Download",
    moveCopy: "Move/Copy",
    askFranklin: "Ask Franklin",
    comparePdfs: "Compare PDFs",
    diffOverview: "Compare IFCs",
    viewDrawing: "View drawing",
    compareDrawings: "Compare drawings",
    moveToTrash: "Delete",
    confirmTrashTitle: "Move to Trash?",
    confirmTrashDescription: "Are you sure you want to move {count} document(s) to trash?",
    clear: "Clear",
    rename: "Rename",
    addNewVersion: "Add new version",
    versioning: "Versioning",
    more: "More",
    verify: "Mark as verified",
    unverify: "Remove verification",
    comments: "Comments",
  },
  comments: {
    title: "Comments",
    openCount: "Open: {count}",
    resolvedCount: "Completed: {count}",
    placeholder: "Write a comment — use @ to mention someone",
    replyPlaceholder: "Write a reply",
    commentOnPage: "Commenting on page {page}",
    commentOnDocument: "Commenting on the document",
    pageAnchorToggle: "Page {page}",
    sheetAnchorToggle: "Sheet: {name}",
    commentOnSheet: "Commenting on the sheet {name}",
    sheet: "Sheet {page}",
    goToSheet: "Go to sheet {page}",
    markComplete: "Mark as complete",
    reopen: "Reopen",
    completedBy: "Completed by {name}",
    completed: "Completed",
    reply: "Reply",
    edit: "Edit",
    edited: "edited",
    save: "Save",
    cancel: "Cancel",
    send: "Send",
    mention: "Mention someone",
    attach: "Attach",
    removeQuote: "Remove quote",
    delete: "Delete",
    confirmDelete: "Delete this reply?",
    confirmDeleteDescription: "The reply will be removed from the thread.",
    deleteThread: "Delete thread",
    confirmDeleteThread: "Delete this thread?",
    confirmDeleteThreadDescription: "The comment and all its replies will be removed.",
    createIssue: "Create issue",
    createIssueOption: "Create issue",
    createIssueOptionHint:
      "Files this comment as a sub-issue under the document's issue. Untick to keep it as a comment only.",
    noProjectForIssue: "This document is not in a project, so it cannot be filed as an issue.",
    issueBacked: "Issue #{number} — replies here are the issue's comments",
    linkedIssue: "Issue #{number}",
    moreActions: "More actions",
    historyAction: "History",
    historyTitle: "Thread history",
    historyEmpty: "No events yet.",
    systemActor: "System",
    history: {
      thread_created: "{name} started the thread",
      comment_added: "{name} replied",
      comment_edited: "{name} edited a comment",
      comment_deleted: "{name} deleted a reply",
      resolved: "{name} marked it as complete",
      reopened: "{name} reopened it",
      issue_created: "{name} created an issue",
      thread_deleted: "{name} deleted the thread",
    },
    page: "p. {page}",
    version: "v{version}",
    viewingVersion: "Viewing",
    goToPage: "Go to page {page}",
    openDocument: "Open document",
    emptyOpen: "No open comments yet.",
    emptyResolved: "No completed comments.",
    unavailable: "Comments are not available yet.",
    loadError: "Comments could not be loaded.",
    writeFailed: "The comment could not be saved.",
    threadUnavailable: "This comment thread is unavailable.",
    issueLinkFailed: "The issue was created but could not be linked to this thread.",
    retry: "Retry",
    rowBadge: "Open comments: {count}",
    unknownUser: "Unknown user",
    justNow: "just now",
    yesterday: "yesterday",
    toggleRail: "Comments",
    origin: "Created from a comment on {filename}",
    originPage: "Created from a comment on {filename} (page {page})",
  },
  verified: {
    // Badge on document rows across Timeline, List and Folders.
    label: "Verified",
    // Hover attribution. {name} is the verifier, {date} the moment they did it.
    tooltip: "Verified by {name} on {date}",
    // Fallback when the verifier's profile could not be resolved.
    tooltipUnknownUser: "Verified on {date}",
    verifiedToast: "Marked {count} document(s) as verified",
    unverifiedToast: "Removed verification from {count} document(s)",
    // Part of the selection was already in the requested state. {count} is what
    // this action changed — and what Undo puts back.
    verifiedToastWithAlready: "Marked {count} document(s) as verified — {already} already verified",
    unverifiedToastWithAlready:
      "Removed verification from {count} document(s) — {already} not verified",
    // Body of the chatbot's confirmation card before it sets or clears the mark.
    confirmVerifyDescription:
      "Records you as the person who checked this version. Adding a new version starts it unverified again.",
    confirmUnverifyDescription: "Clears the Verified mark and who checked it.",
    undo: "Undo",
    error: "Couldn't update the verification. Please try again.",
    // Some rows were rejected (usually missing permission on their project).
    partialError: "Only {count} of {total} document(s) could be updated.",
    // Some rows were rejected and others could not be checked at all. Both are
    // counted out of the whole selection, but only the rejections are claimed.
    partialErrorWithUnknown:
      "Only {count} of {total} document(s) could be updated. {unknown} couldn't be checked — please try again.",
    // Nothing to write — the rows are already the way the action asks for.
    alreadyVerified: "Already verified — nothing to update.",
    alreadyUnverified: "Not verified — nothing to update.",
  },
  empty: {
    dropHere: "Drop files here",
    uploadFirst: "Upload your first documents",
    description:
      "Start by uploading documents to organise your building information. You can drag and drop files here or click the button below.",
    uploadButton: "Upload Documents",
    fileTypes: "Supports PDF, DOC, DOCX, images, and more",
    autoCategorisation: "Automatic categorisation and tagging of files",
    autoCategorisationDesc:
      "Your uploaded documents will be automatically categorised and tagged based on their content, making it easy to find and organise your building information.",
    timelineVersioning: "Timeline view and versioning of your documents",
    timelineVersioningDesc:
      "Track document changes over time with a visual timeline. Easily compare versions and see how your building documentation has evolved.",
    filteredTitle: "No documents match the current filters",
    filteredDescription: "Change or clear the filters and search to see more documents.",
    clearFilters: "Clear filters",
  },
  folders: {
    issueAttachments: "Issue Attachments",
    pinned: {
      cannotRemove: "“{filename}” can't be removed from the {folder} folder.",
      issueAttachmentHint: "Copy it instead, or remove it from its issue.",
      copyInsteadHint: "Copy it instead.",
      moreCannotRemove: "Other files that can't be removed from their folders: {count}",
    },
    createDialog: {
      title: "Create New Folder",
      subfolder: 'Create Subfolder in "{name}"',
      description:
        "Create a new custom folder to organise your documents. You can add subfolders later for better organisation.",
      subfolderDesc:
        'Create a new folder inside "{name}". Subfolders help organise related documents within a main category.',
      nameLabel: "Folder Name",
      namePlaceholder: "Enter folder name...",
      parentLabel: "Parent Folder (Optional)",
      parentPlaceholder: "Select parent folder...",
      noParent: "No parent (root level)",
      nameLengthError: "Folder name must be between 1 and 100 characters",
      invalidChars: 'Folder name cannot contain: < > : " / \\ | ? *',
    },
    unreachableTruncated:
      "Showing {count} of {total} misfiled documents at the project root. Move these into a folder to see the rest.",
  },
  actions2: {
    openMenu: "Open actions menu",
    moveCopyAction: "Move/Copy...",
    rename: "Rename",
  },
  rename: {
    success: "Document renamed",
    successDescription: "Renamed to {name}",
    errorTitle: "Rename failed",
    errorEmpty: "Name cannot be empty",
    errorTooLong: "Name is too long (max {max} characters)",
    errorInvalidChars: 'Name cannot contain: < > : " / \\ | ?',
    errorExtensionChanged: "File extension cannot be changed (must keep {ext})",
    errorCollision: 'A document named "{name}" already exists in this folder',
    errorNotFound: "Document no longer exists",
    errorUnknown: "Could not rename the document",
  },
  dragDrop: {
    prompt: "Drag and drop files or folders here to upload",
    orClick: "or click to browse",
  },
  uploadErrors: {
    fileTooLarge: "This file exceeds the maximum upload size (maximum 50 MB)",
    unsupportedFileType: "This file type is not supported!",
    authError: "Authentication required for upload",
    networkError: "Network error during upload",
    uploadFailed: "Upload failed",
    serverError: "Server error during upload",
    documentRecordFailed: "Failed to create document record",
  },
  uploadProgressTitle: "File Upload Progress",
  uploadFailedMessage: "Upload failed. Please check console for details.",
  categorySelector: {
    allDocumentsTitle: "All Documents & Folders",
    allDocumentsDescription: "Browse all your documents and folders in one place",
    startingClassification: "Starting Classification...",
    aiFileClassification: "AI File Classification",
    advancedOptions: "Advanced classification options",
    buildingIdLabel: "Building ID",
    buildingIdHint: "Building ID for classification (use 0 for all buildings).",
    modeLabel: "Mode",
    modeNew: "New",
    modeAll: "All",
    modeHint: "Classify only new files, or force re-classify all files in the org.",
    scopeLabel: "Scope (days)",
    scopeHint: "Only run on files newer than this number of days.",
    searchLabel: "Search",
    fileTypes: {
      pdf: "PDF Documents",
      images: "Images",
      documents: "Text Documents",
      spreadsheets: "Spreadsheets",
      presentations: "Presentations",
      archives: "Archives",
    },
  },
  table: {
    name: "Name",
    search: "Search",
    searchByName: "Search documents...",
    searchInFolder: "Search in folder...",
    searching: "Searching",
    pressEnterToSearch: "Press Enter to search",
    clearSearch: "Clear search",
    loadingDocuments: "Loading documents...",
    loadError: "Error loading documents",
    noDocumentsFound: "No documents found",
    noSearchResultsHint: "Try a different search term",
    noDocumentsHint: "Upload a document to get started",
    dateCreated: "Date Created",
    dateModified: "File modified",
    fileModifiedColumnTooltip:
      "The creation or modification date stored in the file's own metadata, so it can be earlier than the upload date. Where the file records no date, its upload date is shown.",
    fileModifiedTooltip: "Last modified {date}, according to the file's metadata.",
    fileCreatedTooltip:
      "Created {date}, according to the file's metadata. The file records no modification date.",
    fileDateMissingTooltip:
      "The file's metadata records no date, so this is when it was uploaded to Franklin ({date}).",
    uploadedTooltip:
      "Upload date: when this file was added to Franklin. This is not the date stored in the file itself — see File modified.",
    version: "Version",
    status: "Status",
    user: "User",
    dateUploaded: "Uploaded",
    dateModifiedShort: "Modified",
    dateColumnSwitchLabel: "Show date",
    dateColumnUploadedOption: "Upload date",
    dateColumnModifiedOption: "File modified date",
    dateColumnUploadedHint: "When the file was added to Franklin",
    dateColumnModifiedHint: "From the file's own metadata, so it can predate the upload",
    sortByDate: "Sort by date",
    uploadedAtTooltip: "Uploaded to Franklin {date}.",
    uploader: "Uploader",
    uploaderUnknown: "Uploader unknown",
    uploadedByTooltip: "Uploaded by {name}.",
    tags: "Tags",
    parent: "Parent",
    actions: "Actions",
    selectAll: "Select all documents",
    contentMatch: "Content",
    createdByFranklin: "Created by Franklin",
    contentMatchTooltip: "Found in document text",
    contentMatchOnPage: "Found on page {page}",
    contentMatchOnSheet: "Found on sheet {sheet}",
    contentMatchMore: " (+{count} more)",
    paginationOf: "of",
    previousPage: "Previous page",
    nextPage: "Next page",
  },
  addedFilter: {
    title: "Added",
    hint: "Filters by upload date — when documents were added to Franklin.",
    anyTime: "Any time",
    last7Days: "Last 7 days",
    last30Days: "Last 30 days",
    apply: "Apply",
    clear: "Clear the added date",
  },
  teachFranklin: {
    modalTitle: "Document tags",
    modalDescription: "Help improve AI classification by correcting the tags for",
    successTitle: "Document tags",
    successDescription: "Document tags have been updated successfully.",
    errorTitle: "Error",
    errorDescription: "Failed to update document tags. Please try again.",
    cancel: "Cancel",
    saveChanges: "Save Changes",
    saving: "Saving...",
    tagsSelected: "{count} tag(s) selected",
  },
  tagFilter: {
    searchTags: "Search tags…",
    projects: "Projects",
    categories: "Categories",
    designDisciplines: "Design Disciplines",
    designStages: "Design Stages",
    clearAll: "Clear all",
    noMatches: "No matches for ‘{query}’",
    loadFailed: "Failed to load filtered documents. Showing this folder only.",
    valuesLoadFailed: "Couldn't load filter options.",
    truncated: "Too many matches — showing only the newest. Narrow the filter to see the rest.",
    verifiedOnly: "Verified only",
  },
  viewToggle: {
    folders: "Folders",
    list: "List",
    timeline: "Timeline",
    listView: "List view",
    timelineView: "Timeline view",
  },
  chatSidebar: {
    title: "Franklin",
    subtitle: "Chat with your documents",
    selectChat: "Select chat...",
    chatHistory: "Chat history",
    greetings: {
      personalised: [
        "Hi {firstName}! I'm Franklin.",
        "Hello {firstName} — Franklin here.",
        "Welcome back, {firstName}. Franklin here.",
        "Hi {firstName}. Franklin here, ready when you are.",
        "Good to see you, {firstName}. I'm Franklin.",
      ],
      anonymous: [
        "Hi! I'm Franklin.",
        "Hello — Franklin here.",
        "Welcome back. Franklin here.",
        "Hi. Franklin here, ready when you are.",
        "Good to see you. I'm Franklin.",
      ],
    },
    tips: [
      "Ask me anything about your building.",
      "TIP: Drag a file straight onto this window and I'll read it before you ask.",
      "Did you know? The + button scopes me to a single project — then I only look there.",
      "TIP: I can draft a PDF, Word or Excel file from anything I find.",
      "Did you know? Switch on the globe and I'll search the web alongside your documents.",
      "TIP: I can compare two versions of a document, or two revisions of an IFC file.",
      "Did you know? Franklin opens every IFC file in a 3D view, with storey-by-storey section cuts.",
      "TIP: Add a new version of a document — upload a file or pick one you already have — and the whole history is kept.",
      "Did you know? Two DWG drawings can be compared under Drawings to see exactly what changed.",
      "TIP: Switch Documents to Timeline to see when everything arrived, rather than where it was filed.",
      "Did you know? Documents can be signed in Franklin, and the signed container is filed straight back.",
      "TIP: Share a document with someone outside Franklin using a link that expires.",
    ],
    scopeFallback: "this building",
    examples: {
      docsRecent: "What's in the {count} documents added to {scope} this week?",
      docsRecentOne: "What's in the document added to {scope} this week?",
      docsLatest: "Summarise the latest upload to {scope} — what is it, and what should I know?",
      docsBrief: "Give me a short brief on {scope} from its documents.",
      docsVerified: "Which documents in {scope} have been verified by a user?",
      docsEmpty: "Which documents should be added for {scope}?",
      docsUnavailable: "What should I know about the documents in {scope}?",
      issuesOverdue: "Which of the {count} overdue items in {scope} need immediate attention?",
      issuesOverdueOne: "One item in {scope} is overdue — what does it need?",
      issuesOpen: "Which of the {count} open issues in {scope} should I address first?",
      issuesOpenOne: "There's one open issue in {scope} — what does it need?",
      issuesNew: "Summarise the {count} issues opened in {scope} this week.",
      issuesNewOne: "Summarise the issue opened in {scope} this week.",
      issuesMine: "What's on my plate — walk me through my {count} open issues.",
      issuesMineOne: "Walk me through the open issue assigned to me.",
      issuesSuggest: "Read the newest documents and suggest issues worth opening.",
      issuesEmpty: "Are there unresolved issues in {scope}?",
      modelDiff: "What changed between the last two revisions of {filename}?",
      modelDiffQuantities:
        "Compare the newest IFC file with the previous one — which quantities moved?",
      modelOverview: "Summarise the IFC file for {scope} — storeys, areas and main materials.",
      modelTakeoff: "What's the material takeoff from the IFC file for {scope}?",
      dataMissing: "Which building data fields are still missing for {scope}?",
      dataChanges: "What building data has changed recently, and who changed it?",
      outputPdf: "Draft a PDF summary of {scope} I can send to the client.",
      outputExcel: "Export the open issues in {scope} to an Excel file.",
      outputDocx: "Build a Word document listing what's been filed for {scope} so far.",
      guideCapabilities: "What can you help me with here?",
      guideUpload: "How do I upload and organise documents in Franklin?",
    },
    nudges: {
      processingDone: "{count} documents processed and tagged.",
      processingDoneOne: "1 document processed and tagged.",
      newIssues: "{count} new issues opened since yesterday. Want a summary?",
      newIssuesOne: "1 new issue opened since yesterday. Want a summary?",
      summarise: "Summarise",
      viewIssues: "View issues",
      dismiss: "Dismiss",
      summariseDocumentsPrompt:
        "Summarise the {count} documents that were just processed and tagged — what are they and what should I know about them?",
      summariseIssuesPrompt:
        "Summarise the {count} issues opened in the last 24 hours — what are they about and which needs attention first?",
    },
    drop: {
      attachFiles: "Drop to upload and attach",
      attachDocuments: "Drop to attach to this chat",
      foldersNotAttached: "Folders can't be attached — drag the documents inside them instead.",
      someDocumentsNotAttached: "Some of the documents couldn't be attached.",
      attachFailed: "Couldn't attach the documents. Please try again.",
      readFilesFailed: "Couldn't read the dropped files. Please try again.",
    },
    askPlaceholder: "Ask Franklin...",
    launcherIdle: "Ask Franklin…",
    launcherThinking: "Franklin is thinking",
    openLatestReply: "Open latest reply",
    unreadReply: "1 unread reply",
    loadingChats: "Loading chats...",
    noChatsYet: "No chats yet",
    newChat: "New chat",
    thinking: "Thinking...",
    // Write-tool confirmation card. One turn can raise several, so the card
    // says which of them the user is looking at.
    confirmApprove: "Approve",
    confirmDeny: "Deny",
    confirmAlwaysAllow: "Always allow this action",
    confirmAllowForReply: "Allow for the rest of this reply",
    confirmAllowForReplyHint:
      "Franklin won't ask again for this action until it finishes this reply. It will ask again next time.",
    confirmSaved: "Saved",
    confirmDeclined: "Declined",
    confirmCounter: "{count} requests waiting",
    confirmSubmitFailed: "Couldn't send your decision.",
    confirmExpired: "Franklin has stopped waiting for this one.",
    confirmRetry: "Try again",
    confirmDismiss: "Dismiss",
    statusWorkingOnIt: "Working on it...",
    statusLookingIntoThat: "Let me look into that...",
    statusProcessing: "Processing your request...",
    statusJustAMoment: "Just a moment...",
    addFile: "Add file",
    deleteChatTitle: "Delete Chat?",
    deleteChatDescription: "Are you sure you want to delete this chat?",
    deleteChatConfirm: "Delete",
    messages: "messages",
    expandChat: "Expand chat",
    collapseToSidebar: "Collapse to sidebar",
    closeChat: "Close chat",
    minimizeChat: "Minimize chat",
    editChatName: "Edit chat name",
    archiveChat: "Archive chat",
    sendMessage: "Send message",
    sendingMessage: "Sending message",
    stopGenerating: "Stop generating",
    queuedMessage: "Queued",
    cancelQueuedMessage: "Cancel queued message",
    slowProcessingNote: {
      ifc: "IFC processing can take a few minutes.",
      dwg: "DWG processing can take a few minutes.",
      both: "IFC and DWG processing can take a few minutes.",
      dismiss: "Dismiss",
    },
    composerLimit: {
      counter: "{count} / {limit}",
      counterLabel: "{count} of {limit} characters used",
      overLimit: "Too long to send. Shorten your message to {limit} characters or fewer.",
    },
    sendFailed: {
      tooLong: "Your message is longer than {limit} characters, so it wasn't sent.",
      startFailed:
        "Couldn't start a new chat, so your message wasn't sent. It's still in the box below — please try again.",
      notSent: "Your message couldn't be sent. It's still in the box below — please try again.",
      uploadFailed:
        "A file couldn't be uploaded, so your message wasn't sent. It's back in the box below with the files that did upload.",
      dismiss: "Dismiss",
    },
    editFailed: {
      title: "Failed to edit message",
      description: "Could not remove the previous exchange. Please try again.",
    },
    disclaimerText: "Franklin is AI and can make mistakes. Please double-check responses.",
    webSearch: {
      toggle: "Toggle web search",
      enabled: "Web search enabled",
      disabled: "Web search disabled",
    },
    attachMenu: {
      upload: "Upload",
      addFromFranklin: "Add from Franklin",
      openFiles: "Open chat files",
      workInProject: "Work in a project",
    },
    projectScope: {
      active: "Working in project: {name}",
      remove: "Stop working in this project",
      pickerTitle: "Work in a project",
    },
  },
  shareDialog: {
    title: "Create Share Link",
    description: "Create a temporary link to share {description} with anyone.",
    titleLabel: "Title (optional)",
    titlePlaceholder: "e.g., Project drawings for review",
    expiryLabel: "Link expires in",
    createButton: "Create Link",
    creating: "Creating...",
    linkCreated: "Share link created!",
    linkCreatedToast: "Share link created",
    linkReadyToast: "Your share link is ready to copy.",
    linkCopied: "Link copied",
    linkCopiedToast: "Share link copied to clipboard.",
    failedCreate: "Failed to create share link",
    failedCopy: "Failed to copy",
    copyManually: "Please copy the link manually.",
    copyLink: "Copy Link",
    copied: "Copied!",
    expiryNote:
      "This link will expire in {expiry}. Anyone with the link can view and download the shared documents.",
    document: "document",
    documents: "documents",
    folder: "folder",
    folders: "folders",
    and: "and",
    expiryOptions: {
      oneHour: "1 hour",
      twentyFourHours: "24 hours",
      sevenDays: "7 days",
      thirtyDays: "30 days",
    },
  },
  manageSharesDialog: {
    title: "Manage Share Links",
    description: "View and manage document share links for this building.",
    failedLoad: "Failed to load shares",
    emptyTitle: "No share links created yet",
    emptyHint: "Select documents and click Share to create a link",
    activeHeading: "Active ({count})",
    inactiveHeading: "Inactive ({count})",
    statusActive: "Active",
    statusExpired: "Expired",
    statusRevoked: "Revoked",
    untitledShare: "Untitled share",
    docSingular: "{count} doc",
    docPlural: "{count} docs",
    viewSingular: "{count} view",
    viewPlural: "{count} views",
    createdAgo: "Created {time}",
    copyLinkTitle: "Copy link",
    revokeShareTitle: "Revoke share",
    linkCopiedToastTitle: "Link copied",
    linkCopiedToastDesc: "Share link copied to clipboard.",
    copyFailedToastTitle: "Failed to copy",
    copyFailedToastDesc: "Please copy the link manually.",
    revokedToastTitle: "Share revoked",
    revokedToastDesc: "The share link is no longer accessible.",
    revokeFailedToastTitle: "Failed to revoke share",
    revokeFailedToastDesc: "Please try again",
    revokeConfirmTitle: "Revoke share link?",
    revokeConfirmDesc:
      "This will immediately disable the share link. Anyone with the link will no longer be able to access the shared documents.",
    revokeButton: "Revoke",
    revoking: "Revoking...",
    expiry: {
      dayLeft: "{count} day left",
      daysLeft: "{count} days left",
      hourLeft: "{count} hour left",
      hoursLeft: "{count} hours left",
      minuteLeft: "{count} minute left",
      minutesLeft: "{count} minutes left",
    },
  },
  picker: {
    title: "Select documents",
    submit: "Add",
    project: "Project",
    selectProject: "Select a project…",
    defaultProject: "(default)",
    files: "Files",
    projectFallback: "Project",
    loadingFolders: "Loading folders",
    foldersFailed: "Couldn't load folders.",
    noFolders: "No folders yet",
    nothingSelected: "Nothing selected",
    selectedOne: "1 document selected",
    selectedMany: "{count} documents selected",
    projectsFailed: "Couldn't load projects. Close the dialog and try again.",
    selectProjectPrompt: "Select a project to browse documents",
    filterPlaceholder: "Filter files…",
    subfolderSearchFailed: "Couldn't search subfolders — showing this folder only.",
    folderDocumentsFailed: "Couldn't load this folder's documents.",
    searchTruncated: "Showing the first {limit} matches — keep typing to narrow the search.",
    retry: "Retry",
    loadingDocuments: "Loading documents",
    showEarlierVersions: "Show earlier versions",
    hideEarlierVersions: "Hide earlier versions",
    loadingEarlierVersions: "Loading earlier versions…",
    noEarlierVersions: "No earlier versions available",
    attached: "Attached",
    documentsFailed: "Couldn't load documents.",
    tryAgain: "Try again",
    emptyFolder: "No documents in this folder",
  },
  versioning: {
    collisionDialog: {
      titleSingle: "1 file already exists in this folder",
      titleMany: "{count} files already exist in this folder",
      subtitle:
        "Choose how to handle each one. You can upload as a new version, keep both as separate files, or skip.",
      selectAllAria: "Select all collisions",
      selectRowAria: "Select {filename}",
      bulk: {
        selectAll: "Select all {count}",
        nSelected: "{count} selected",
        applyTo: "Apply to {target}:",
        applyToAll: "all",
      },
      row: {
        existingMeta: "Existing: v{version}",
        uploaderLine: "{relTime} by {uploader}",
        uploaderLineNoName: "{relTime}",
        sizeDiff: "{oldSize} → {newSize}",
        sizeSingle: "{size}",
      },
      actions: {
        version: {
          label: "New version",
          short: "New version",
          tooltip:
            "Upload as the next version of the existing file (v2, v3, …). The previous version is preserved in the version history.",
        },
        duplicate: {
          label: "Keep both",
          short: "Duplicate",
          tooltip:
            "Upload as a brand-new document. Filename gets a (1), (2), … suffix. Both files stay at v1.",
        },
        skip: {
          label: "Skip",
          short: "Skip",
          tooltip: "Don't upload this file. The existing file stays untouched.",
        },
      },
      tally: {
        newVersion: "new version",
        duplicate: "duplicate",
        skipped: "skipped",
      },
      confirmSingle: "Upload 1 file",
      confirmMany: "Upload {count} files",
      confirmNone: "Nothing to upload",
    },
    manage: {
      menuItem: "Versioning",
      dialogTitle: "Versioning",
      dialogDescription:
        '"{filename}" — newest version at the top. Drag to change the order; the top one is the current version.',
      preview: "Preview this version",
      addVersions: "Add versions",
      backToVersions: "Back to versions",
      current: "Current",
      becomesCurrent: "Becomes the current version",
      dragHandle: "Drag to reorder",
      moveUp: "Move up",
      moveDown: "Move down",
      makeSeparate: "Make separate file",
      makeSeparateTooltip:
        "Take this version out of the history. It becomes a file of its own in the same folder.",
      keepAsVersion: "Keep as version",
      separateBadge: "Becomes a separate file",
      lastRemainingHint: "At least one version has to stay in the history.",
      separateNotPermitted:
        "Some versions in the trash are where you can't change files, so none can be made separate.",
      editNotPermitted: "You can view these versions, but you don't have access to change them.",
      loading: "Loading versions…",
      loadError: "The versions could not be loaded.",
      retry: "Try again",
      save: "Save changes",
      saving: "Saving…",
      noChanges: "No changes",
      editUnavailable:
        "Reordering and separating versions isn't available yet. You can still add versions.",
      toastSavedTitle: "Versions updated",
      toastReorderedDescription: 'The new order of "{filename}" is saved.',
      toastDetachedDescription: "Versions now separate files: {count}",
      toastRenamedTitle: "Separate file renamed",
      toastRenamedDescription:
        '"{from}" was renamed to "{to}", because that name is already in the folder.',
      toastPartialTitle: "Not every change was saved",
      discardTitle: "Discard your changes?",
      discardDescription:
        "The new order and the versions you marked as separate files won't be saved.",
      discardWithStagedDescription:
        "Your unsaved changes here, and the files you staged to add as new versions, will be lost.",
      discardStagedDescription: "The files you staged to add as new versions will be lost.",
      keepEditing: "Keep editing",
      discard: "Discard",
      errors: {
        unavailable: "Reordering and separating versions isn't available yet.",
        versionsChanged: "The versions changed while you were editing. The list has been reloaded.",
        forbidden: "You don't have permission to change every version of this document.",
        notFound: "This document could not be found. It may have been deleted.",
        notVersionable: "This document's versions can't be changed here.",
        lastVersion: "At least one version has to stay in the history.",
        headInOtherProject:
          "That version is filed in another project, so it can't become the current version.",
        pinned: "A pinned file can't leave its folder.",
        unknown: "The changes could not be saved. Please try again.",
      },
    },
    addNewVersion: {
      menuItem: "Add new version",
      dialogTitle: "Add new versions",
      dialogDescription: '"{filename}" is currently v{version}.',
      dragDropFiles: "Drag and drop files here",
      dropFilesHere: "Drop files here",
      orClickToBrowse: "or click to browse",
      addMoreFiles: "Add more files",
      chooseFromDocuments: "Choose from documents",
      sortByName: "Sort by name",
      becomesCurrent: "Becomes the current version",
      moveUp: "Move up",
      moveDown: "Move down",
      removeFile: "Remove",
      mismatchBadge: "Different name",
      mismatchTooltip: 'This filename doesn\'t match "{existing}". It will still be added.',
      blockedTitle: "File type not allowed",
      blockedDescription: "{count} file(s) were rejected because their type is not allowed.",
      confirmNone: "Nothing to add",
      confirmSingle: "Add 1 version",
      confirmMany: "Add {count} versions",
      adding: "Adding…",
      fromProject: "From {project}",
      versionChip: "v{version}",
      modeLabel: "How to add this document",
      modeMove: "Move",
      modeCopy: "Copy",
      moveHintSingle: "Moves the document here",
      moveHintMany: "Moves all {count} versions here",
      copyHint: "Adds a copy; the original stays where it is",
      moveDisabledOlderVersion:
        "Only the current version can be moved — moving brings the whole history with it.",
      moveDisabledDuplicate: "This document's history is already in the list.",
      crossProjectMoveWarning:
        "This document is in {project}. Its comment issues will stay there, and issues pinned on the drawing will be unpinned.",
      checkingHistory: "Checking history…",
      historyFailed: "Couldn't read this document's history. Remove it and try again.",
      pickerTitle: "Choose documents to add",
      pickerDescription:
        "Pick from any project in this building. You can expand a document to pick an earlier version.",
      pickerSubmit: "Add to list",
      pickerTargetLineage: "Already part of this document's history",
      pickerAlreadyAdded: "In the list",
      toastSuccessTitle: "New version added",
      toastSuccessDescription: '"{filename}" saved as v{version}.',
      toastMovedDescription: '"{filename}" and its history were added as v{first}–v{last}.',
      toastMovedSingleDescription: '"{filename}" was moved in as v{version}.',
      toastCopiedDescription: 'A copy of "{filename}" was added as v{version}.',
      toastBatchSuccessTitle: "New versions added",
      toastBatchSuccessDescription: "{count} new versions added — v{first} to v{last}.",
      toastPartialFailureTitle: "Stopped part-way",
      toastPartialFailureDescription:
        'Stopped at "{filename}". {count} earlier item(s) were added and are unaffected; the rest are still listed so you can try again.',
      toastRenamedTitle: "Renamed to avoid a clash",
      toastRenamedDescription:
        '"{from}" was saved as "{to}" because another document in this folder already has that name.',
      toastCrossProjectTitle: "Moved from another project",
      toastDetachedDescription: "Its comment issues stay in the project it came from.",
      toastAnchorsRemovedDescription: "{count} issue pin(s) on the drawing were removed.",
      errorNoVersionGroup:
        "This document has no version group — cannot add a new version. Try re-uploading the file instead.",
      errorNoProject: "No project context for this document — cannot add a new version.",
      errors: {
        unavailable:
          "Adding existing documents as versions isn't available yet. You can still upload files.",
        forbiddenSource: "You don't have permission to move that document.",
        forbiddenTarget: "You don't have permission to add versions to this document.",
        sameLineage: "That document is already part of this document's history.",
        differentBuilding: "Only documents from the same building can be added.",
        sourceNotFound: "That document is no longer available.",
        targetNotFound: "This document is no longer available.",
        targetNotVersionable: "This document can't take new versions.",
        sourceIsChild: "A file inside another document can't be added on its own.",
        containerCrossProject:
          "This archive has folders of its own and can't be moved to another project. Copy it instead.",
        pinned: "That file can't be removed from its folder. Copy it instead.",
        unknown: "Something went wrong while adding the document.",
      },
    },
    history: {
      modalTitle: "Version History",
      viewFullHistory: "View full history",
      current: "Current",
      empty: "No versions found",
      loadError: "Failed to load version history",
      close: "Close",
      fetchFailedTitle: "Failed to load versions",
      fetchFailedDescription: "Could not fetch older versions of this file.",
      compareHint: "Select two versions to compare",
      select: "Select",
      selectAll: "Select all versions",
      selectedCount: "{count} selected",
      versionsCount: "{count} versions",
    },
    toasts: {
      filesSkippedTitle: "Files skipped",
      filesSkippedDescription: "{count} file(s) skipped due to filename collisions.",
      identicalSkippedTitle: "Skipped identical files",
      identicalSkippedDescription:
        "{count} file(s) already present with identical content — nothing to upload.",
    },
    preview: {
      unavailableTitle: "Preview Unavailable",
      versionUnavailable: "This file version is no longer available.",
      dwgNotConverted: "DWG file conversion not complete. Child PDF not found.",
    },
  },
  organize: {
    button: "Organise",
    title: "Organise folder",
    titleProject: "Organise project",
    description:
      "Franklin analyses your files in this folder and proposes a new subfolder structure.",
    scopeLoose: "Loose files only",
    scopeLooseHint:
      "Sort the {count} files sitting directly in this folder. Existing subfolders are left untouched.",
    scopeSubtree: "Whole folder tree",
    scopeSubtreeHint:
      "Reorganise all {count} files, including those inside the {folders} existing subfolders.",
    start: "Start analysis",
    // Tags mode has no analysis and nothing to wait for: the structure is
    // already on screen, and this only moves on to the file-level review.
    startTags: "Continue",
    loading: "Checking this folder's organise status…",
    loadingProposal: "Loading the proposed structure…",
    queued: "Queued — waiting for the analysis to start…",
    analyzing: "Analysing the folder's documents… This can take a few minutes.",
    closeHint: "You can close this window, Franklin will let you know once he's done.",
    inProgressPill: "Organising…",
    reviewPill: "Review proposal",
    failedTitle: "Organisation failed",
    retry: "Try again",
    cancel: "Cancel",
    reviewStats: "Files to move: {moves} · Folders: {folders} ({newFolders} new)",
    newFolderBadge: "new",
    existingFolderBadge: "existing",
    keptFiles: "Staying where they are: {count}",
    moveCount: "{selected} of {total} moves selected",
    expiryNote: "This proposal expires {time}.",
    apply: "Apply",
    applying: "Applying…",
    discard: "Discard",
    appliedToast: "Moved {moved} files into {folders} folders.",
    skippedStaleWarning:
      "{count} files were skipped because they changed after the proposal was made.",
    expiredError: "This proposal has expired. Run the analysis again.",
    alreadyRunning:
      "This folder already has an organise run in progress or awaiting review, possibly a teammate's. It has to be finished or discarded first.",
    alreadyRunningProject:
      'An organise run is already under way in a folder of this project. Finish or discard that one first, or choose "Loose files only".',
    alreadyRunningWholeProject:
      "An organise run covering the whole project is already under way. Finish or discard that one first.",
    alreadyRunningParent:
      "An organise run on a parent folder already covers this folder. Finish or discard that one first.",
    alreadyRunningSubfolder:
      'An organise run is already under way in a subfolder. Finish or discard that one first, or choose "Loose files only".',
    startError: "Could not start the analysis.",
    applyError: "Could not apply the proposal.",
    cancelError: "Could not discard the proposal.",
    countsError: "Could not load the folder's file counts.",
    namesError: "Could not load file names — showing document numbers instead.",
    requestError: "Could not check this folder's organise status.",
    readyToast: "Folder organisation is ready for review.",
    readyToastAction: "Review",
    additionalInstructions: "Additional instructions",
    additionalInstructionsPlaceholder:
      'Optional — e.g. "keep all photos together" or "use numeric prefixes for folders"',
    modeAgent: "Franklin decides",
    modeAgentHint: "Franklin reads the files and proposes a structure that fits them.",
    modeTags: "By tags",
    modeTagsHint: "Group by the tags your documents already carry. Instant, and no guessing.",
    groupBy: "Group by",
    groupByHint: "Folders nest in the order you pick. Click a tag to add or remove it.",
    dimensionPosition: "Level {position}",
    previewLabel: "Resulting structure",
    previewLoading: "Working out the structure…",
    previewEmpty: "None of these files carry the tags you picked, so there is nothing to move.",
    previewError: "Could not work out the structure.",
    previewStats: "Files to move: {moves} · Folders: {folders}",
    tagCapWarning:
      "This would create {count} folders, past the limit of {limit}. Remove a level to continue.",
    ambiguousNote: "More than one tag on a level, so the first was used: {count}",
    untaggedNote: "Files with no tag for a level stay at the level above.",
    tagSummary: "Grouped by {dimensions}.",
    noDimensions: "Pick at least one tag to group by.",
    renameFolder: "Rename folder",
    previewFile: "Preview file",
    dropToKeep: "Drop here to leave the file where it is",
    renameInvalidEmpty: "A folder needs a name.",
    renameInvalidTooLong: "Keep the name under {max} characters.",
    renameInvalidSlash: 'A folder name cannot contain "/".',
    renameInvalidDuplicate: "Another folder in the same place already has this name.",
    renamedBadge: "renamed",
    pathLabel: "Folder being organised",
    pathEllipsisTitle: "Hidden folders: {names}",
    containerBlocked: "Leave this archive to organise a folder.",
  },
  signedContainer: {
    signed: "Signed",
    signedExternally: "Signed outside Franklin",
    signatureIssue: "Signature issue",
    containerType: "Signed container (ASiC-E)",
    inContainerChip: "in {ext}",
    showEnclosed: "Show enclosed documents",
    extracting: "Extracting files...",
    panel: {
      eyebrow: "Signature details",
      enclosedTitle: "Documents",
      enclosedEmpty: "Franklin holds no record of what this container encloses.",
      enclosedError: "Could not load the documents in this container.",
      viewSignatures: "View signatures",
      inspectionChecking: "Checking signatures…",
      inspectionChecked: "Signatures checked with Dokobit.",
      inspectionCheckedAt: "Checked {date}",
      inspectionRetry: "Check again",
      inspectionFailed: "Could not check signatures. Try again in a minute.",
      inspectionUnsupported: "Signature checking is not supported for this file type.",
      inspectionTooLarge: "Signature checking supports files up to 20 MB.",
      noSignatures: "No digital signatures were found in this file.",
      unknownSigner: "Signer name unavailable",
      unknownTime: "Signing time unavailable",
      timestampOn: "Signature timestamp: {date}",
      fileType: "{type} file",
      signaturePassed: "Passed signature checks",
      signatureWarnings: "Validation warnings: {count}",
      unsigned: "No digital signatures",
      signaturesTitle: "Signatures",
      signaturesEmpty:
        "Franklin holds no signature details for this container — it was signed outside Franklin, or has not been processed yet.",
      signatureBroken: "Signature check failed",
      fieldStatus: "Status",
      fieldSignatures: "Signatures",
      fieldSignaturesUnknown: "Details unavailable",
      fieldSignedOn: "Signed on",
      signedOn: "Signed {date}",
      openedFrom: "Opened from {document} inside {container}.",
      sign: "Add signature",
      share: "Share",
      open: "Open",
      download: "Download",
      close: "Close",
      loading: "Loading signature details...",
      loadError: "Could not load the signature details for this container.",
      downloadFailed: "Download failed. Please try again.",
      openFailed: "Could not open the document. Please try again.",
    },
  },
};
